import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import ClientLayout from "../../components/client/ClientLayout";
import { useAuth } from "../../context/AuthContext";
import { clientApi } from "../../api";

const POLL_MS = 15000;

export default function LiveFloor() {
  const { user } = useAuth();
  const isAdmin = user?.clientRole === "admin";
  const [egms, setEgms] = useState([]);
  const [floor, setFloor] = useState(null);
  const [polling, setPolling] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [manageOpen, setManageOpen] = useState(false);

  const loadFloor = useCallback(async () => {
    try {
      const d = await clientApi.getLiveFloor();
      setFloor(d);
      setError("");
    } catch (err) { setError(err.message); }
  }, []);

  const loadEgms = useCallback(async () => {
    try {
      const d = await clientApi.listEgms();
      setEgms(d.egms || []);
    } catch (err) { setError(err.message); }
  }, []);

  useEffect(() => {
    Promise.all([loadFloor(), loadEgms()]).finally(() => setLoading(false));
  }, [loadFloor, loadEgms]);

  useEffect(() => {
    if (!polling) return;
    const handle = setInterval(loadFloor, POLL_MS);
    return () => clearInterval(handle);
  }, [polling, loadFloor]);

  const occupied = (floor?.floor || []).filter((e) => e.inUse).length;
  const total = floor?.floor?.length || 0;
  const areas = groupByArea(floor?.floor || []);

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>Live Floor</h1>
          <p className="page-subtitle">{total > 0 ? `${occupied} of ${total} EGMs in use` : "No EGMs registered yet"}</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button className={`btn btn-sm ${polling ? "btn-primary" : "btn-outline"}`} onClick={() => setPolling((p) => !p)}>
            {polling ? "Live · auto-refresh" : "Paused"}
          </button>
          <button className="btn btn-outline btn-sm" onClick={loadFloor}>Refresh now</button>
          {isAdmin && <button className="btn btn-outline" onClick={() => setManageOpen(true)}>Manage EGMs</button>}
        </div>
      </div>

      {error && <div className="toast toast-error" style={{ marginBottom: "1rem" }}>{error}</div>}
      {loading && <div style={{ color: "var(--text-muted)" }}>Loading floor…</div>}

      {!loading && !total && (
        <div className="empty-state">
          <h3>No EGMs configured</h3>
          <p>Register the machines at this venue so we can poll S7000 for who's playing them.</p>
          {isAdmin && <button className="btn btn-primary" onClick={() => setManageOpen(true)}>Add EGMs</button>}
        </div>
      )}

      {Object.entries(areas).map(([area, list]) => (
        <div key={area} style={{ marginTop: "1.5rem" }}>
          <h3 style={{ margin: "0 0 0.75rem" }}>{area}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0.75rem" }}>
            {list.map((e) => <EgmTile key={e.egm_id} egm={e} />)}
          </div>
        </div>
      ))}

      {floor?.at && (
        <div style={{ marginTop: "1rem", fontSize: "0.8rem", color: "var(--text-muted)" }}>
          Last polled {new Date(floor.at).toLocaleTimeString()}{polling && " · refreshes every 15s"}
        </div>
      )}

      {manageOpen && (
        <EgmManager
          egms={egms}
          onClose={() => { setManageOpen(false); loadEgms(); loadFloor(); }}
        />
      )}
    </ClientLayout>
  );
}

function EgmTile({ egm }) {
  const color = egm.error ? "var(--danger)" : egm.inUse ? "var(--success)" : "var(--text-muted)";
  const label = egm.error ? "Error" : egm.inUse ? "Occupied" : "Free";
  const m = egm.member;
  return (
    <div className="settings-card" style={{ padding: "0.8rem 1rem", borderLeft: `4px solid ${color}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontWeight: 600 }}>{egm.label || <code>{egm.egm_id}</code>}</div>
        <span style={{ fontSize: "0.75rem", textTransform: "uppercase", color, fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>EGM <code>{egm.egm_id}</code></div>
      {m && (
        <Link to={`/client/members/${m.membershipId}`} style={{ display: "block", marginTop: "0.5rem", fontSize: "0.85rem", color: "inherit", textDecoration: "none" }}>
          <div style={{ fontWeight: 500 }}>{m.firstName} {m.lastName}</div>
          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>#{m.membershipId}</div>
        </Link>
      )}
      {egm.error && <div style={{ marginTop: "0.5rem", fontSize: "0.75rem", color: "var(--danger)" }}>{egm.error}</div>}
    </div>
  );
}

function groupByArea(floor) {
  const out = {};
  for (const e of floor) {
    const a = e.area || "Main Floor";
    if (!out[a]) out[a] = [];
    out[a].push(e);
  }
  return out;
}

function EgmManager({ egms: initialEgms, onClose }) {
  const [egms, setEgms] = useState(initialEgms);
  const [egm_id, setEgmId] = useState("");
  const [label, setLabel] = useState("");
  const [area, setArea] = useState("");
  const [bulkRange, setBulkRange] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!egm_id) return setError("EGM id required");
    setBusy(true); setError("");
    try {
      await clientApi.upsertEgm({ egm_id, label, area });
      setEgmId(""); setLabel(""); setArea("");
      const d = await clientApi.listEgms(); setEgms(d.egms || []);
    } catch (err) { setError(err.message); }
    setBusy(false);
  }

  async function bulkAdd() {
    if (!bulkRange) return;
    const ids = expandRange(bulkRange);
    if (!ids.length) return setError("Enter e.g. '100-120' or '100,101,105'");
    setBusy(true); setError("");
    try {
      await clientApi.bulkUpsertEgms(ids.map((id) => ({ egm_id: id, area })));
      setBulkRange("");
      const d = await clientApi.listEgms(); setEgms(d.egms || []);
    } catch (err) { setError(err.message); }
    setBusy(false);
  }

  async function remove(id) {
    if (!window.confirm("Remove this EGM?")) return;
    try { await clientApi.deleteEgm(id); setEgms((xs) => xs.filter((x) => x.id !== id)); }
    catch (err) { alert(err.message); }
  }

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <div className="modal-header-text">
            <h2>Manage EGMs</h2>
            <p>Register each gaming machine so the live floor view can poll S7000.</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="modal-body">
          {error && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{error}</div>}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "0.5rem", alignItems: "end", marginBottom: "0.75rem" }}>
            <div className="form-field"><label>EGM id</label><input value={egm_id} onChange={(e) => setEgmId(e.target.value)} placeholder="100" /></div>
            <div className="form-field"><label>Label (optional)</label><input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Dragon 88" /></div>
            <div className="form-field"><label>Area (optional)</label><input value={area} onChange={(e) => setArea(e.target.value)} placeholder="Main Floor" /></div>
            <button className="btn btn-primary" disabled={busy} onClick={add}>Add</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr auto", gap: "0.5rem", alignItems: "end", marginBottom: "1rem", padding: "0.75rem", background: "var(--bg-elevated)", borderRadius: 8 }}>
            <div className="form-field">
              <label>Bulk add by range or list</label>
              <input value={bulkRange} onChange={(e) => setBulkRange(e.target.value)} placeholder="100-120  or  100,101,105" />
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>Will use Area "{area || "—"}".</div>
            </div>
            <button className="btn btn-outline" disabled={busy} onClick={bulkAdd}>Bulk add</button>
          </div>

          {egms.length ? (
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>EGM</th><th>Label</th><th>Area</th><th></th></tr></thead>
                <tbody>
                  {egms.map((e) => (
                    <tr key={e.id}>
                      <td><code>{e.egm_id}</code></td>
                      <td>{e.label || <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                      <td>{e.area || <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                      <td><button className="btn btn-danger btn-sm" onClick={() => remove(e.id)}>Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={{ color: "var(--text-muted)" }}>No EGMs yet.</div>}
        </div>
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function expandRange(s) {
  const out = [];
  for (const part of s.split(",").map((p) => p.trim()).filter(Boolean)) {
    const m = part.match(/^(\d+)-(\d+)$/);
    if (m) {
      const a = Number(m[1]), b = Number(m[2]);
      for (let i = Math.min(a, b); i <= Math.max(a, b); i++) out.push(String(i));
    } else if (/^\d+$/.test(part)) {
      out.push(part);
    }
  }
  return out;
}
